import { parseOptionalNumber } from '../../../lib/parseOptionalNumber';
import { z } from 'zod';

import { dashboardT } from '../../../lib/messages';
import { targetingFormSchema } from '../../targeting/model';
import { customTargetingExtFormSchema } from '../../targeting/customTargetingExtModel';
import {
  formatDateTimeInputForPayload,
  parseDateTimeInput,
  type LineItemDateTimeOptions,
} from './lineItemDateTimeModel';
import { CampaignStatus } from './model';
import {
  MadBuyFrequencyCappingTimeUnit,
  MadBuyLineItemPacing,
  MadBuyLineItemType,
  madBuyLineItemWeekDays,
} from './lineItemsCoreModel';

const dayTimeActivitySchema = z.looseObject({
  day: z.enum(madBuyLineItemWeekDays),
  restrictedHours: z.boolean(),
  startHour: z.number(),
  endHour: z.number(),
});

const creativeAssociationSchema = z.looseObject({
  id: z.string().optional(),
  creative: z.looseObject({
    id: z.string(),
    name: z.string(),
    type: z.string().optional(),
    altId: z.string().optional(),
  }),
  weight: z.number(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});

const lineItemFormBaseSchema = z.object({
  name: z.string(),
  status: z.enum(CampaignStatus),
  adType: z.enum(MadBuyLineItemType),
  cpm: z.string(),
  impressionsGoal: z.string(),
  domain: z.string(),
  margin: z.string(),
  upstreamCpmCapEnabled: z.boolean(),
  upstreamCpmCapMinCpm: z.string(),
  upstreamCpmCapMaxCpm: z.string(),
  impressionTrackersEnabled: z.boolean(),
  impressionTrackers: z.array(z.string()),
  frequencyCappingsEnabled: z.boolean(),
  fcLimit: z.string(),
  fcDuration: z.string(),
  fcTimeUnit: z.enum(MadBuyFrequencyCappingTimeUnit),
  startDate: z.string(),
  endDate: z.string(),
  scheduling: z.boolean(),
  pacing: z.enum(MadBuyLineItemPacing),
  pacingPerHour: z.enum(MadBuyLineItemPacing),
  dayTimeActivities: z.array(dayTimeActivitySchema),
  targeting: targetingFormSchema.extend({
    userLanguages: z.array(
      targetingFormSchema.shape.userLanguages.element.extend({
        isoCode2: z.string().optional(),
        isoCode3: z.string().optional(),
      }),
    ),
  }),
  customTargetingExt: customTargetingExtFormSchema,
  lineItemCreatives: z.array(creativeAssociationSchema),
  conversionPixelId: z.string(),
  // Uploaded through the ZIP/IP CSV import after the line item is saved; never part of the PUT.
  pendingZipCodesCsv: z.custom<File>().nullable(),
  pendingIpAddressesCsv: z.custom<File>().nullable(),
});

export type MadBuyLineItemGeneralForm = z.infer<typeof lineItemFormBaseSchema>;

export function createMadBuyLineItemFormSchema(options: LineItemDateTimeOptions = {}) {
  return lineItemFormBaseSchema.superRefine((form, context) => {
    const addIssue = (field: keyof MadBuyLineItemGeneralForm, message: string) => {
      context.addIssue({ code: 'custom', path: [field], message });
    };
    const name = form.name.trim();
    const cpm = parseOptionalNumber(form.cpm);
    const impressionsGoal = parseOptionalNumber(form.impressionsGoal);
    const margin = parseOptionalNumber(form.margin);
    const minCap = parseOptionalNumber(form.upstreamCpmCapMinCpm);
    const maxCap = parseOptionalNumber(form.upstreamCpmCapMaxCpm);
    const fcLimit = parseOptionalNumber(form.fcLimit);
    const fcDuration = parseOptionalNumber(form.fcDuration);
    const startDate = parseDateTimeInput(form.startDate);
    const endDate = parseDateTimeInput(form.endDate);

    if (!name) addIssue('name', dashboardT('madBuy.lineItems.validation.nameRequired'));
    else if (name.length > 255) addIssue('name', dashboardT('madBuy.lineItems.validation.nameMax'));
    if (cpm === undefined || cpm < 0)
      addIssue('cpm', dashboardT('madBuy.lineItems.validation.cpmRequired'));
    if (impressionsGoal === undefined || impressionsGoal < 0)
      addIssue('impressionsGoal', dashboardT('madBuy.lineItems.validation.impressionsRequired'));
    if (form.margin.trim() && (margin === undefined || margin < 0 || margin > 100))
      addIssue('margin', dashboardT('madBuy.lineItems.validation.percentRange'));
    if (form.domain.trim() && !isValidDomain(form.domain.trim()))
      addIssue('domain', dashboardT('madBuy.lineItems.validation.domainInvalid'));
    if (form.upstreamCpmCapEnabled) {
      if (minCap === undefined || minCap < 0)
        addIssue('upstreamCpmCapMinCpm', dashboardT('madBuy.lineItems.validation.minCpmInvalid'));
      if (maxCap === undefined || maxCap < 0)
        addIssue('upstreamCpmCapMaxCpm', dashboardT('madBuy.lineItems.validation.maxCpmInvalid'));
      if (
        minCap !== undefined &&
        minCap >= 0 &&
        maxCap !== undefined &&
        maxCap >= 0 &&
        minCap > maxCap
      )
        addIssue(
          'upstreamCpmCapMaxCpm',
          dashboardT('madBuy.lineItems.validation.minCpmExceedsMax'),
        );
    }
    if (form.frequencyCappingsEnabled) {
      if (!isValidFrequencyValue(fcLimit))
        addIssue('fcLimit', dashboardT('madBuy.lineItems.validation.frequencyLimitInvalid'));
      if (!isValidFrequencyValue(fcDuration))
        addIssue('fcDuration', dashboardT('madBuy.lineItems.validation.frequencyDurationInvalid'));
    }
    if (!form.startDate)
      addIssue('startDate', dashboardT('madBuy.lineItems.validation.startDateRequired'));
    else if (!startDate || !isExistingZonedDateTime(form.startDate, options.timezone))
      addIssue('startDate', dashboardT('madBuy.lineItems.validation.startDateInvalid'));
    if (!form.endDate)
      addIssue('endDate', dashboardT('madBuy.lineItems.validation.endDateRequired'));
    else if (!endDate || !isExistingZonedDateTime(form.endDate, options.timezone))
      addIssue('endDate', dashboardT('madBuy.lineItems.validation.endDateInvalid'));
    else if (startDate && compareDateTimeParts(startDate, endDate) >= 0)
      addIssue('endDate', dashboardT('madBuy.lineItems.validation.endDateAfterStart'));
    form.customTargetingExt.groups.forEach((group, groupIndex) => {
      group.rules.forEach((rule, ruleIndex) => {
        if (rule.values.length === 0) {
          context.addIssue({
            code: 'custom',
            path: ['customTargetingExt', 'groups', groupIndex, 'rules', ruleIndex, 'values'],
            message: dashboardT('madBuy.lineItems.validation.customTargetingValuesRequired'),
          });
        }
      });
    });
    if (
      form.lineItemCreatives.some(
        (association) =>
          !association.creative.id ||
          !association.creative.name ||
          !Number.isFinite(association.weight) ||
          association.weight < 0 ||
          association.weight > 100,
      )
    ) {
      addIssue(
        'lineItemCreatives',
        dashboardT('madBuy.lineItems.validation.creativeAssociationsInvalid'),
      );
    }
  });
}

function isValidFrequencyValue(value: number | undefined) {
  return value !== undefined && Number.isSafeInteger(value) && value >= 0;
}

function isExistingZonedDateTime(value: string, timezone = 'UTC') {
  try {
    formatDateTimeInputForPayload(value, timezone);
    return true;
  } catch {
    return false;
  }
}

function compareDateTimeParts(
  left: NonNullable<ReturnType<typeof parseDateTimeInput>>,
  right: NonNullable<ReturnType<typeof parseDateTimeInput>>,
) {
  return (
    Date.UTC(left.year, left.month - 1, left.day, left.hour, left.minute) -
    Date.UTC(right.year, right.month - 1, right.day, right.hour, right.minute)
  );
}

function isValidDomain(value: string) {
  return /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)([/\w-.?%&=]*)?$/.test(value);
}
